/**
 * The lord himself: his profile, his realm, his claims, his inbox.
 *
 * Everything here is about the player rather than about one of his villages,
 * which is why it does not live in the village store. The realm badges on the
 * village bar, the profile page, the world picker and the inbox all read from
 * this one place, so a claim forged on the map is reflected in the badge
 * without either of them knowing the other exists.
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { PrivateLetter, RealmClaim, TitleTier } from '@waldermarch/shared';

import { ApiRequestError, api } from '@/utils/api';
import { useNotificationStore } from '@/stores/notifications';

export interface RealmVassal {
  id: string;
  name: string;
  points: number;
  villages: number;
}

export interface MyProfile {
  id: string;
  name: string;
  bio: string;
  avatar: string | null;
  points: number;
  createdAt: string;
  battlesWon: number;
  campsCleared: number;
  realm: {
    domain: {
      held: number;
      limit: number;
      over: number;
      productionMultiplier: number;
      loyaltyDrainPerHour: number;
    };
    best: TitleTier | null;
    prestige: number;
    claims: RealmClaim[];
    titles: { counties: number[]; duchies: number[]; kingdoms: number[]; empires: number[] };
    liege: { id: string; name: string } | null;
    vassals: RealmVassal[];
  };
  costs: Record<TitleTier, number>;
  maxClaims: number;
}

export interface GameServer {
  id: string;
  name: string;
  online: boolean;
  players: number;
  villages: number;
  played: boolean;
  points: number;
  holdings: number;
}

export const useRealmStore = defineStore('realm', () => {
  const notifications = useNotificationStore();

  const profile = ref<MyProfile | null>(null);
  const servers = ref<GameServer[]>([]);
  const letters = ref<PrivateLetter[]>([]);
  const unread = ref(0);
  const loading = ref(false);

  /** Claims that have not lapsed, soonest to expire first. */
  const claims = computed(() =>
    [...(profile.value?.realm.claims ?? [])].sort((a, b) => a.expiresAt - b.expiresAt),
  );
  const prestige = computed(() => profile.value?.realm.prestige ?? 0);
  const domain = computed(() => profile.value?.realm.domain ?? null);

  function report(err: unknown, fallback: string): void {
    notifications.error(err instanceof ApiRequestError ? err.message : fallback);
  }

  /**
   * `quiet` is for the background poll that keeps the top bar honest.
   *
   * A failed refresh once a minute is not news: the player did not ask for it,
   * nothing they can see is wrong yet, and a toast every minute during a blip
   * would bury the ones that matter. A refresh the player *did* ask for still
   * says so.
   */
  async function load(quiet = false): Promise<void> {
    if (!quiet) loading.value = true;
    try {
      profile.value = await api.get<MyProfile>('/realm/me');
    } catch (err) {
      if (!quiet) report(err, 'Could not read your profile.');
    } finally {
      if (!quiet) loading.value = false;
    }
  }

  async function loadServers(): Promise<void> {
    try {
      servers.value = (await api.get<{ servers: GameServer[] }>('/realm/servers')).servers;
    } catch (err) {
      report(err, 'Could not reach the world list.');
    }
  }

  async function save(patch: { bio?: string; avatar?: string }): Promise<boolean> {
    try {
      const next = await api.patch<{ bio: string; avatar: string | null }>('/realm/me', patch);
      if (profile.value) {
        profile.value.bio = next.bio;
        profile.value.avatar = next.avatar;
      }
      notifications.success('Profile saved.');
      return true;
    } catch (err) {
      report(err, 'Could not save your profile.');
      return false;
    }
  }

  async function forgeClaim(tier: TitleTier, id: number): Promise<boolean> {
    try {
      await api.post('/realm/claims', { tier, id });
      notifications.success('Claim forged.');
      await load();
      return true;
    } catch (err) {
      report(err, 'Could not forge that claim.');
      return false;
    }
  }

  // --- the inbox ---------------------------------------------------------------

  async function loadLetters(quiet = false): Promise<void> {
    try {
      const data = await api.get<{ letters: PrivateLetter[]; unread: number }>(
        '/players/me/letters',
      );
      letters.value = data.letters;
      unread.value = data.unread;
    } catch (err) {
      if (!quiet) report(err, 'Could not open your letters.');
    }
  }

  /**
   * Answer an offer of fealty.
   *
   * The server checks the whole arrangement again before it binds anything,
   * because a realm can change completely while a letter sits unread. An offer
   * that can no longer be honoured comes back marked void with the reason,
   * which is worth showing rather than swallowing: "he swore to somebody else
   * an hour ago" is the answer to the question the player was about to ask.
   */
  async function answerOffer(letterId: string, accept: boolean): Promise<void> {
    try {
      const { offer } = await api.post<{ offer: { status: string; voidReason?: string } }>(
        `/realm/fealty/answer/${letterId}`,
        { accept },
      );
      if (offer.status === 'void') {
        notifications.error(offer.voidReason ?? 'That offer is no longer valid.');
      } else if (offer.status === 'accepted') {
        notifications.success('Fealty accepted.');
      } else {
        notifications.success('Offer declined.');
      }
      await Promise.all([loadLetters(), load()]);
    } catch (err) {
      report(err, 'Could not answer that offer.');
    }
  }

  async function offerFealty(
    toUserId: string,
    kind: 'takeMeAsVassal' | 'becomeMyVassal',
    body?: string,
  ): Promise<boolean> {
    try {
      await api.post('/realm/fealty/offer', { toUserId, kind, body });
      notifications.success('Offer sent.');
      return true;
    } catch (err) {
      report(err, 'Could not send that offer.');
      return false;
    }
  }

  async function release(userId: string): Promise<void> {
    try {
      await api.post(`/realm/fealty/release/${userId}`, {});
      notifications.success('Fealty ended.');
      await load();
    } catch (err) {
      report(err, 'Could not end that fealty.');
    }
  }

  return {
    profile,
    servers,
    letters,
    unread,
    loading,
    claims,
    prestige,
    domain,
    load,
    loadServers,
    save,
    forgeClaim,
    loadLetters,
    answerOffer,
    offerFealty,
    release,
  };
});
